/**
 * Lightweight inline SVG sparkline. No Recharts — the plan doc calls
 * for "Recharts line charts" on Page timeline, but Recharts is not
 * in the dashboard-v2 package.json. Shipping a self-contained 40-line
 * SVG path keeps the bundle lean and preserves the visual intent
 * (trend-at-a-glance) without adding a ~60kB dependency.
 *
 * Flagged in the Stage 5 handover as a deviation: if we actually
 * need interactive tooltips / axis labels, install Recharts or a
 * smaller alternative (e.g. `@visx/sparkline`) in Stage 7 polish.
 */
import { cn } from '@/lib/utils';
import type { ReactElement } from 'react';

export interface SparklineDatum {
  /** X-axis value; typically a timestamp. */
  x: number;
  /** Y-axis value; null points produce gaps. */
  y: number | null;
}

export function Sparkline({
  data,
  width = 320,
  height = 64,
  stroke = 'var(--color-accent-brand)',
  fill = 'var(--color-accent-brand)',
  fillOpacity = 0.08,
  className,
  ariaLabel,
}: {
  data: SparklineDatum[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  fillOpacity?: number;
  className?: string;
  ariaLabel?: string;
}): ReactElement {
  const valid = data.filter((d) => d.y != null) as { x: number; y: number }[];
  if (valid.length === 0) {
    return (
      <div
        className={cn(
          'flex h-16 items-center justify-center text-xs text-[var(--color-mute)]',
          className,
        )}
        aria-label={ariaLabel}
      >
        No data yet
      </div>
    );
  }
  const xs = valid.map((d) => d.x);
  const ys = valid.map((d) => d.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;

  const points = valid.map((d) => {
    const x = ((d.x - minX) / spanX) * (width - 4) + 2;
    const y = height - 2 - ((d.y - minY) / spanY) * (height - 4);
    return `${x},${y}`;
  });

  const linePath = `M${points.join(' L')}`;
  const areaPath = `${linePath} L${width - 2},${height - 2} L2,${height - 2} Z`;

  return (
    <svg
      className={cn('block', className)}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
    >
      <title>{ariaLabel ?? 'Sparkline'}</title>
      <path d={areaPath} fill={fill} fillOpacity={fillOpacity} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}
